import { Map, Record, Seq } from 'immutable';

import { bindToDispatch } from './dispatch';
import { Module } from './module';
import {
  IEffect,
  IMiddleware,
  IModule,
  IModuleParams,
  IPayloadlessEffect,
  IStatefulEffect,
  IStatefulPayloadlessEffect,
  IStateMachine,
  ISubscriber
} from './types';

import { effect, IComposeEffectMapCallbackParams } from './core/effects/composeEffects';

type IActionReducer<AP, AS> = (state: AS, payload: AP) => AS;
type IPayloadlessActionReducer<AS> = (state: AS) => AS;

type IActionCreator<AT, AP> = (actionPayload: AP) => IAction<AT, AP>;
type IPayloadlessActionCreator<AT> = () => IAction<AT, void>;

interface IActionComposer<RT> {
  reducerType: RT;
}

interface IReducerlessActionComposer<AT, AP> extends IActionComposer<'none'> {
  actionType: AT;
  create: IActionCreator<AT, AP>;
}

interface IComposeActionReducer<RT, AT, AR> extends IActionComposer<RT> {
  actionType: AT;
  reduce: AR;
}

type IComposePayloadActionReducer<AT, AR> = IComposeActionReducer<'payload', AT, AR>;
type IComposePayloadlessActionReducer<AT, AR> = IComposeActionReducer<'payloadless', AT, AR>;
type IComposeAnyActionReducer<AT, AR> = IComposePayloadActionReducer<AT, AR> | IComposePayloadlessActionReducer<AT, AR>;

interface IPayloadActionComposer<AT, AP, AS> extends IComposePayloadActionReducer<AT, IActionReducer<AP, AS>> {
  create: IActionCreator<AT, AP>;
}

interface IPayloadlessActionComposer<AT, AS>
  extends IComposePayloadlessActionReducer<AT, IPayloadlessActionReducer<AS>> {
  create: IPayloadlessActionCreator<AT>;
}

export type IAnyActionComposer<AT, AP, AS> =
  | IPayloadActionComposer<AT, AP, AS>
  | IPayloadlessActionComposer<AT, AS>
  | IReducerlessActionComposer<AT, AP>;

export type IReducerActionComposer<AT, AP, AS> =
  | IPayloadActionComposer<AT, AP, AS>
  | IPayloadlessActionComposer<AT, AS>;

export type IAnyActionComposerMap<AS> = { [key: string]: IAnyActionComposer<any, any, AS> };

export function composeActionCreators<ACM extends { [K in keyof ACM]: { create } }>(
  actionMap: ACM
): { [K in keyof ACM]: ACM[K]['create'] } {
  return Object.keys(actionMap).reduce((actionCreators, key) => {
    return Object.assign(actionCreators, { [key]: bindToDispatch(actionMap[key].create) });
  }, {} as { [K in keyof ACM]: ACM[K]['create'] });
}

export function composeInitialState<T extends object>(initialState: T) {
  return Record(initialState)();
}

interface IComposeActionMapCallbackParams<AS> {
  action<AP>(reduce: IActionReducer<AP, AS>): IPayloadActionComposer<string, AP, AS>;

  payloadlessAction(reduce: IPayloadlessActionReducer<AS>): IPayloadlessActionComposer<string, AS>;
}

interface IComposeReducerActionsCallbackParams<AS> {
  actionReducer<AT, AP, AR extends IActionReducer<AP, AS>>(
    actionComposer: IPayloadActionComposer<AT, AP, any>,
    reduce: AR
  ): IComposePayloadActionReducer<AT, AR>;

  payloadlessActionReducer<AT, AR extends IPayloadlessActionReducer<AS>>(
    actionComposer: IAnyActionComposer<AT, any, any>,
    reduce: AR
  ): IComposePayloadlessActionReducer<AT, AR>;
}

export function composeReducer<AS>(
  actionMap: IAnyActionComposerMap<AS>,
  reducerCallback?: (compose: IComposeReducerActionsCallbackParams<AS>) => IComposeAnyActionReducer<any, any>[]
) {
  function actionReducer<AT, AR extends IActionReducer<any, AS>>(
    actionComposer: IPayloadActionComposer<AT, any, any>,
    reduce: AR
  ): IComposePayloadActionReducer<AT, AR> {
    return { reducerType: 'payload', actionType: actionComposer.actionType, reduce };
  }

  function payloadlessActionReducer<AT, AR extends IPayloadlessActionReducer<AS>>(
    actionComposer: IAnyActionComposer<AT, any, any>,
    reduce: AR
  ): IComposePayloadlessActionReducer<AT, AR> {
    return { reducerType: 'payloadless', actionType: actionComposer.actionType, reduce };
  }

  const actions: Seq.Indexed<IReducerActionComposer<any, any, AS>> = Map(actionMap)
    .valueSeq()
    .concat(reducerCallback ? reducerCallback({ actionReducer, payloadlessActionReducer }) : [])
    .filter((ac) => ac.reducerType !== 'none') as Seq.Indexed<IReducerActionComposer<any, any, AS>>;

  return (state: AS, action: IAction<any, any>): AS => {
    const reducer = actions.find((ac) => ac.actionType === action.type);

    if (!reducer) {
      return state;
    }

    if (reducer.reducerType === 'payload') {
      return reducer.reduce(state, action.payload);
    }

    return reducer.reduce(state);
  };
}

export const createNamespacer =
  (...args) =>
  (actionType: string) =>
    [...args, actionType].join('/');

export const composeModule = <MN extends string, MV extends string>(moduleName: MN, moduleVersion: MV) => {
  const moduleNamespace = createNamespacer(moduleName, moduleVersion);
  const composeStateMachine = <RN extends string, AS, RETURN extends IAnyActionComposerMap<AS>>(
    reducerName: RN,
    initialState: AS,
    callback: (compose: IComposeActionMapCallbackParams<AS>) => RETURN,
    reducerCallback?: (compose: IComposeReducerActionsCallbackParams<AS>) => IComposeAnyActionReducer<any, any>[]
  ): IStateMachine<AS, RETURN> => {
    const reducerNamespace = createNamespacer(reducerName);
    const reducerBranchKey = moduleNamespace(reducerName);
    const namespacer = (k: string) => moduleNamespace(reducerNamespace(k));

    function action<AP>(reduce: IActionReducer<AP, AS>): IPayloadActionComposer<string, AP, AS> {
      return {
        reducerType: 'payload',
        actionType: '',
        reduce,
        create: (payload: AP) => ({ type: '', payload })
      };
    }

    function payloadlessAction(reduce: IPayloadlessActionReducer<AS>): IPayloadlessActionComposer<string, AS> {
      return {
        reducerType: 'payloadless',
        actionType: '',
        reduce: reduce,
        create: () => ({ type: '' })
      };
    }

    const actionMap = callback({ action, payloadlessAction });

    const actions = Object.keys(actionMap).reduce((result, key) => {
      return Object.assign(result, {
        [key]: Object.assign({}, actionMap[key], {
          actionType: namespacer(key),
          create:
            actionMap[key].reducerType === 'payload'
              ? (payload) => ({ type: namespacer(key), payload })
              : () => ({ type: namespacer(key) })
        })
      });
    }, {} as typeof actionMap);

    return {
      actions,
      actionCreators: composeActionCreators(actions),
      branchKey: reducerBranchKey,
      initialState,
      reducer: composeReducer(actions, reducerCallback),
      selector: (state) => {
        return state.get(reducerBranchKey) as AS;
      }
    };
  };

  const composeEffects = <RETURN>(
    namespace: string,
    callback: (compose: IComposeEffectMapCallbackParams) => RETURN
  ): { [K in keyof RETURN]: RETURN[K] } => {
    const namespacer = createNamespacer(moduleName, namespace);

    function tempEffect<EP, ER>(run: (eff: EP) => Promise<ER>): IEffect<string, EP, ER> {
      return { kind: 'effect', run } as any;
    }

    function tempPayloadlessEffect<ER>(run: () => Promise<ER>): IPayloadlessEffect<string, ER> {
      return { kind: 'payloadlessEffect', run } as any;
    }

    function tempStatefulEffect<EP, ER, SR>(
      selector: (state: Map<string, any>) => SR,
      run: (state: SR, eff: EP) => Promise<ER>
    ): IStatefulEffect<string, EP, ER, SR> {
      return { kind: 'statefulEffect', selector, run } as any;
    }

    function tempStatefulPayloadlessEffect<ER, SR>(
      selector: (state: Map<string, any>) => SR,
      run: (state: SR) => Promise<ER>
    ): IStatefulPayloadlessEffect<string, ER, SR> {
      return { kind: 'statefulPayloadlessEffect', selector, run } as any;
    }

    const actionMap = callback({
      effect: tempEffect,
      payloadlessEffect: tempPayloadlessEffect,
      statefulEffect: tempStatefulEffect,
      statefulPayloadlessEffect: tempStatefulPayloadlessEffect
    });

    return Object.keys(actionMap).reduce((result, key) => {
      return Object.assign(result, {
        [key]: effect(namespacer(key), actionMap[key].run, actionMap[key].kind, actionMap[key].selector)
      });
    }, {} as RETURN);
  };

  const composeMiddleware = (middleware: IMiddleware) => middleware;
  const composeSubscriber = <SR>(subscriber: ISubscriber<SR>) => subscriber;

  return {
    composeStateMachine,
    composeEffects,
    composeMiddleware,
    composeSubscriber,
    namespace: createNamespacer(moduleName),
    exportModule: <P, T, S extends IAnyActionComposerMap<T>>(mod: IModuleParams<P, T, S>): IModule<P, T, S> =>
      Module({ ...mod, name: `${moduleName}/${moduleVersion}` }) as IModule<P, T, S>
  };
};
